/**
 * Talking to the server.
 *
 * Every call carries the session cookie and every failure comes back as one sentence a
 * person can act on, because these messages are read at a counter with a customer
 * waiting, not in a developer console.
 */

export type Role = "sales_staff" | "supervisor" | "manager" | "admin" | "master";

export interface User {
  readonly userId: string;
  readonly personId: string;
  readonly username: string;
  readonly fullName: string;
  readonly role: Role;
  readonly mustChangePassword: boolean;
}

export interface SessionInfo {
  readonly user: User;
  readonly device: { readonly code: string } | null;
}

export type ProgressState = "done" | "building" | "waiting";

export interface ProgressItem {
  readonly code: string;
  readonly label: string;
  readonly state: ProgressState;
  readonly note?: string;
}

export interface ProgressStage {
  readonly key: string;
  readonly title: string;
  readonly items: readonly ProgressItem[];
}

export interface Progress {
  readonly quotation: string;
  readonly startedOn: string;
  readonly updatedOn?: string;
  readonly recentUpdates?: readonly string[];
  readonly stages: readonly ProgressStage[];
  readonly totals: { readonly done: number; readonly total: number };
  readonly waitingOnYou: readonly string[];
}

export type OwnerKind = "company" | "person";
export type AccountKind = "bank" | "cash" | "crypto" | "card";
export type ContactKind = "supplier" | "customer" | "both";

export interface Account {
  readonly id: string;
  readonly ownerKind: OwnerKind;
  readonly ownerPersonId: string | null;
  readonly ownerName: string;
  readonly name: string;
  readonly kind: AccountKind;
  readonly currency: string;
  readonly institution: string | null;
  readonly openingBalanceCents: number;
  readonly openingDate: string;
  readonly isActive: boolean;
  readonly notes: string | null;
}

export interface Contact {
  readonly id: string;
  readonly name: string;
  readonly kind: ContactKind;
  readonly isCompany: boolean;
  readonly phone: string | null;
  readonly email: string | null;
  readonly uen: string | null;
  readonly notes: string | null;
  readonly address?: string | null;
  readonly website?: string | null;
}

export interface Person {
  readonly id: string;
  readonly code: string;
  readonly fullName: string;
  readonly jobTitle: string | null;
}

export interface NewAccountInput {
  readonly ownerKind: OwnerKind;
  readonly ownerPersonId?: string | null;
  readonly name: string;
  readonly kind: AccountKind;
  readonly currency?: string;
  readonly institution?: string | null;
  readonly openingBalance?: string;
  readonly openingDate: string;
}

export interface NewContactInput {
  readonly name: string;
  readonly kind: ContactKind;
  readonly isCompany?: boolean;
  readonly phone?: string | null;
  readonly uen?: string | null;
}

export type Direction = "money_in" | "money_out" | "stock";

export interface Category {
  readonly id: string;
  readonly name: string;
  readonly direction: Direction;
  readonly isActive: boolean;
  readonly useCount: number;
  /** The heading it sits under (D, 15 October 2026). */
  readonly groupId?: string | null;
  readonly groupName?: string | null;
  /** Under a group of stock bought to sell: shown on Inventory purchases. */
  readonly forResale?: boolean;
  /** The money filed under it, bank lines and expense lines together, as a positive amount. */
  readonly amountCents?: number;
  /** The shop's product category whose purchases it covers, such as Sealed Products. */
  readonly productCategory?: string | null;
  /** How many bank and cash movements are under it. */
  readonly entryCount?: number;
}

/** A bank line put to a category, or a line of a purchase under it. */
export interface CategoryEntry {
  readonly day: string;
  readonly kind: "bank" | "cash";
  readonly reference: string;
  readonly description: string;
  readonly amountCents: number;
}

/** A heading over categories, such as Inventory Expenses or General Expenses. */
export interface CategoryGroup {
  readonly id: string;
  readonly name: string;
  readonly direction: Direction;
  readonly forResale: boolean;
}

/** Returned instead of a new category when the name looks like one that exists. */
export interface CategorySuggestion {
  readonly id: string;
  readonly name: string;
}

export interface AccountBalance {
  readonly accountId: string;
  readonly accountName: string;
  readonly ownerName: string;
  readonly currency: string;
  readonly openingBalanceCents: number;
  readonly openingDate: string;
  readonly ourBalanceCents: number;
  readonly bankBalanceCents: number | null;
  readonly differenceCents: number | null;
  readonly lineCount: number;
  readonly unmatchedCount: number;
  readonly lastLineDate: string | null;
}

export interface BalanceSummary {
  readonly accounts: AccountBalance[];
  readonly outOfBalance: AccountBalance[];
  readonly unmatchedTotal: number;
  readonly companyTotals: { readonly currency: string; readonly totalCents: number }[];
}

export type DeviceKind = "ipad" | "laptop" | "desktop" | "server";

/** What a device says about itself. Known from its token, never from anything typed. */
export interface ThisDevice {
  readonly code: string;
  readonly name: string;
  readonly kind: DeviceKind;
}

export interface Device {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly kind: DeviceKind;
  readonly isActive: boolean;
  readonly isRegistered: boolean;
  readonly lastSeenAt: string | null;
  readonly registeredAt: string | null;
  readonly claimExpiresAt: string | null;
  /** Where its cash sales are paid into unless the person selling picks another. */
  readonly cashAccountId: string | null;
  /** The same for PayNow. */
  readonly paynowAccountId: string | null;
}

export type ItemType = "box" | "pack" | "etb" | "bundle" | "single" | "accessory" | "other" | "case" | "display" | "collection" | "tin";

export interface Product {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly category: string;
  /** Pokemon, Lorcana, Riftbound: how a customer asks for a thing. */
  readonly game: string | null;
  readonly setName: string | null;
  readonly language: string | null;
  readonly itemType: ItemType;
  readonly packsPerBox: number | null;
  /** What one of these holds, and how many: a case its boxes, a box its packs. */
  readonly containsProductId?: string | null;
  readonly containsQuantity?: number | null;
  /** A few words on what it is, once somebody has read and kept them. */
  readonly description?: string | null;
  readonly defaultPriceCents: number | null;
  /**
   * What one costs the shop on average, as the shop states it, and the sum behind it:
   * "[(7 x 226) + (3 x 240)] / (7 + 3) = 230.20000". The price below which a sale loses
   * money, which is why it sits beside the price rather than with the costs.
   */
  readonly averageCostCents: number | null;
  readonly averageCostNote: string | null;
  readonly priceSetOn: string | null;
  /** Kept at the top of its shelf on the booth screen. */
  readonly pinned: boolean;
  /** Where it sits inside its category on the booth screen: Packs, Bundles and so on. */
  readonly section: string | null;
  /** Held and not sold: on the stock list as ever, greyed on the booth screen. */
  readonly notForSale: boolean;
  /** The newest picture of it, shown on its tile. */
  readonly photoId: string | null;
  /** One unit's cost averaged over every company batch on hand; null when none is. */
  readonly stockAverageCostCents?: number | null;
  readonly stockAverageOver?: number;
  readonly isComplete: boolean;
  readonly isActive: boolean;
  /** FALSE for something sold at a typed amount, with nothing counted behind it. */
  readonly tracksStock: boolean;
  readonly onHand: number;
  /** Units sold on TikTok, and in the shop and at the booth, in paid sales. */
  readonly soldTikTok?: number;
  readonly soldElsewhere?: number;
}

export interface PictureSuggestion { readonly token: string; readonly sourceUrl: string; readonly caption: string }

export interface Location {
  readonly id: string;
  readonly name: string;
  readonly kind: "shop" | "office" | "home" | "show" | "other";
  readonly keptByName: string | null;
  readonly isActive: boolean;
}

/** Cost fields are absent, not zero, for a role that may not see them. */
export interface Lot {
  /** Flagged for review, shown first (D, 14 October 2026). */
  readonly flagged?: boolean;
  readonly flagNote?: string | null;
  readonly id: string;
  readonly kind: "quantity" | "slab";
  readonly productId: string | null;
  readonly productCode: string | null;
  readonly productName: string | null;
  readonly purchaseBatchRef: string | null;
  readonly acquiredOn: string;
  readonly acquisition: string;
  readonly isFree: boolean;
  readonly ownerKind: OwnerKind;
  readonly ownerPersonId: string | null;
  readonly ownerName: string;
  readonly locationId: string | null;
  readonly locationName: string | null;
  readonly quantityOnHand: number;
  readonly totalCostCents?: number;
  readonly averageCostCents?: number;
  readonly isComplete: boolean;
  readonly isVerified: boolean;
  readonly verifiedByName: string | null;
  readonly verifiedAt: string | null;
  readonly slabName: string | null;
  readonly slabLanguage?: string | null;
  readonly certNo: string | null;
  /** A slab's own photo, or its product's picture. */
  readonly photoId?: string | null;
  /** Where to cover a slab's cert number on that photo; null when it is shown. */
  readonly certCover?: { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number } | null;
}

export interface SlabDetails {
  readonly lotId: string;
  readonly certNo: string | null;
  readonly grader: string | null;
  readonly grade: string | null;
  readonly gradeDescription: string | null;
  readonly cardName: string | null;
  readonly setName: string | null;
  readonly yearReleased: number | null;
  readonly cardNumber: string | null;
  readonly language: string | null;
  readonly rarity: string | null;
  readonly era: string | null;
  readonly labelText: string | null;
  /** Where the cert number sits on the slab's photo. */
  readonly certBox: { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number } | null;
  /** The cert number is covered whenever the photo is shown. */
  readonly hideCert: boolean;
  readonly sellingPriceCents: number | null;
  readonly priceSetOn: string | null;
}

export interface SlabInput {
  readonly gradeDescription?: string | null;
  readonly certNo?: string | null;
  readonly grader?: string | null;
  readonly grade?: string | null;
  readonly cardName?: string | null;
  readonly setName?: string | null;
  readonly yearReleased?: number | null;
  readonly cardNumber?: string | null;
  readonly language?: string | null;
  readonly rarity?: string | null;
  /** The printed year, region and era: 2019 P.M. JPN. SUN & MOON. */
  readonly era?: string | null;
  /** The whole label as read, everything but the barcode. */
  readonly labelText?: string | null;
  readonly certBox?: { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number } | null;
  readonly hideCert?: boolean;
  readonly sellingPrice?: string | null;
}

export interface StockTotals {
  readonly lots: number;
  readonly units: number;
  readonly valueCents: number;
}

export interface StockView {
  readonly lots: Lot[];
  readonly value: { company: StockTotals; personal: StockTotals; free: StockTotals } | null;
  readonly attention: { incomplete: number; negative: number };
  readonly seesCost: boolean;
}

export interface Movement {
  readonly id: string;
  readonly happenedOn: string;
  readonly kind: string;
  readonly quantity: number;
  readonly costCents?: number;
  readonly reason: string | null;
  readonly actorName: string | null;
  readonly deviceCode: string | null;
}

export interface PurchaseLineInput {
  readonly productId?: string | null;
  readonly isSlab?: boolean;
  readonly description: string;
  readonly quantity: number;
  readonly lineTotal: string;
  readonly isComplete?: boolean;
  /** A graded card's label, read from its photo on the form. */
  readonly slab?: SlabInput | null;
  /** What the supplier's invoice called this line, remembered against the product picked. */
  readonly invoiceWords?: string | null;
  /** Not stock for resale: the money-out category it goes to. */
  readonly categoryId?: string | null;
  /** A product not on the list yet, made with the purchase (D, 14 October 2026). */
  readonly newProduct?: {
    readonly name: string;
    readonly itemType: ItemType;
    readonly category: string;
    readonly language?: string | null;
    readonly section?: string | null;
  } | null;
}

export interface NewPurchaseInput {
  readonly supplierPersonId?: string | null;
  readonly purchasedOn: string;
  readonly supplierContactId?: string | null;
  readonly supplierName?: string | null;
  readonly paidFromAccountId: string;
  readonly purchaseBatchRef?: string | null;
  readonly supplierInvoiceNo?: string | null;
  readonly lines: readonly PurchaseLineInput[];
  readonly notes?: string | null;
  readonly supplierDetails?: SupplierDetails | null;
}

export interface Purchase {
  /** Flagged for review, shown first (D, 14 October 2026). */
  readonly flagged?: boolean;
  readonly flagNote?: string | null;
  readonly supplierPersonId?: string | null;
  readonly id: string;
  readonly number: string;
  readonly purchasedOn: string;
  readonly supplierName: string;
  readonly paidFromAccountName: string;
  readonly ownerKind: OwnerKind;
  readonly ownerName: string;
  readonly purchaseBatchRef: string | null;
  readonly supplierInvoiceNo: string | null;
  readonly totalCents: number;
  readonly status: "received" | "cancelled";
  readonly isVerified: boolean;
  readonly voucherNumber: string | null;
  readonly supplierContactId: string | null;
  readonly paidFromAccountId: string;
  readonly cancelReason: string | null;
  readonly attachments: readonly Attachment[];
  readonly lines: readonly {
    lineNo: number;
    lotId: string | null;
    categoryId?: string | null;
    categoryName?: string | null;
    /** Its category is under a group of stock bought to sell. */
    categoryForResale?: boolean;
    productId: string | null;
    isSlab: boolean;
    slab?: SlabInput | null;
    description: string;
    quantity: number;
    lineTotalCents: number;
  }[];
}

export interface Voucher {
  readonly isVerified?: boolean;
  readonly paidFromAccountKind?: string;
  readonly hasBankMatches?: boolean;
  readonly id: string;
  readonly number: string;
  readonly issuedOn: string;
  readonly payeeName: string;
  readonly amountCents: number;
  readonly paidFromAccountName: string;
  readonly description: string;
  readonly status: "unpaid" | "paid" | "cancelled";
  readonly paidOn: string | null;
  readonly purchaseNumber: string | null;
  readonly partPaidCents: number | null;
  /** Each bank payment on it, when it was paid in more than one. */
  readonly payments?: readonly { readonly day: string; readonly amountCents: number }[];
  /** The photos of what a buyback's voucher paid for. */
  readonly photoIds?: readonly string[];
}

export interface ImportPreview {
  readonly accountId: string;
  readonly accountName: string;
  readonly accountNumber: string | null;
  readonly from: string;
  readonly to: string;
  readonly lineCount: number;
  readonly newLineCount: number;
  readonly alreadyHereCount: number;
  readonly beforeOpeningCount: number;
  readonly moneyInCents: number;
  readonly moneyOutCents: number;
  readonly openingBalanceCents: number | null;
  readonly closingBalanceCents: number | null;
  readonly warnings: readonly string[];
  readonly importedBefore: { readonly on: string; readonly by: string } | null;
}

export interface ImportResult extends ImportPreview {
  readonly importId: string | null;
}

export interface BankImport {
  readonly id: string;
  readonly accountName: string;
  readonly fileName: string | null;
  readonly from: string | null;
  readonly to: string | null;
  readonly lineCount: number;
  readonly newLineCount: number;
  readonly closingBalanceCents: number | null;
  readonly importedAt: string;
  readonly importedBy: string;
}

export interface BankLine {
  /** The three best matches, nearest in date first among equals (D, 18 October 2026). */
  readonly suggestions?: readonly MatchSuggestion[];
  /** Its transaction category, for the accounts (D, 16 October 2026). */
  readonly accountingCategory?: string | null;
  readonly categoryConfirmed?: boolean;
  /** Each category's part, from the purchase's own lines (D, 21 October 2026). */
  readonly categoryParts?: readonly { readonly label: string; readonly amountCents: number }[];
  /** Up to three categories to confirm, best first (D, 16 October 2026). */
  readonly categoryChoices?: readonly { readonly id: string; readonly label: string }[];
  /** Flagged for review, shown first (D, 14 October 2026). */
  readonly flagged?: boolean;
  readonly flagNote?: string | null;
  readonly id: string;
  readonly accountId: string;
  readonly accountName: string;
  readonly postDate: string;
  readonly valueDate: string;
  readonly kind: string | null;
  readonly description: string;
  readonly counterparty: string | null;
  readonly payerNote: string | null;
  readonly bankReference: string | null;
  readonly amountCents: number;
  readonly balanceAfterCents: number | null;
  readonly categoryName: string | null;
  readonly isMatched: boolean;
  readonly isIgnored: boolean;
  readonly match: MatchInfo | null;
  readonly suggestion: MatchSuggestion | null;
}

export type MatchKind = "voucher" | "purchase" | "sale" | "category";

export interface MatchInfo {
  readonly kind: string;
  readonly id: string;
  readonly label: string;
}

export interface MatchSuggestion {
  readonly kind: MatchKind;
  readonly id: string;
  readonly label: string;
  readonly reason: string;
  readonly score: number;
}

/** Where the AI reading's key comes from; the key itself never comes back to a screen. */
export interface AiKeyState {
  readonly source: "desk" | "server" | "off";
  readonly lastFour: string | null;
  readonly changedAt: string | null;
  readonly changedBy: string | null;
}

/** A photo or a PDF kept with a bank line or a supplier purchase. */
export interface Attachment {
  readonly id: string;
  readonly contentType: string;
}

export interface BankLineDetail {
  readonly id: string;
  readonly accountId: string;
  readonly postDate: string;
  readonly amountCents: number;
  readonly counterparty: string | null;
  readonly match: MatchInfo | null;
  readonly matchNote: string | null;
  readonly matchedBy: string | null;
  readonly matchedAt: string | null;
  readonly isIgnored: boolean;
  readonly ignoreReason: string | null;
  readonly suggestions: readonly MatchSuggestion[];
  readonly sameCounterpartyOpen: number;
  /** For money out: what a transfer of this amount, near this day, was said to be for. */
  readonly transferNotes?: readonly { readonly at: string; readonly description: string }[];
  readonly attachments: readonly Attachment[];
  readonly matchedDocument: {
    readonly number: string;
    readonly day: string;
    readonly party: string | null;
    readonly totalCents: number;
    readonly paidCents: number | null;
    readonly payments?: readonly { readonly day: string; readonly amountCents: number; readonly thisLine: boolean }[];
    readonly photoIds?: readonly string[];
    readonly pdfIds?: readonly string[];
    readonly items: readonly string[];
    readonly expenseItems?: readonly number[];
    readonly subtotalCents?: number | null;
    readonly discountCents?: number | null;
    readonly surchargeCents?: number | null;
  } | null;
  readonly options: {
    readonly vouchers: readonly { id: string; label: string }[];
    readonly purchases: readonly { id: string; label: string }[];
    readonly sales: readonly { id: string; label: string; amountCents?: number }[];
    readonly categories: readonly { id: string; label: string }[];
  };
}

export interface BankLinePage {
  readonly lines: BankLine[];
  readonly total: number;
  readonly page: number;
  readonly pages: number;
}

export interface StatementFile {
  readonly accountId: string;
  readonly fileName: string;
  readonly content: string;
}

/** What one sale cost, line by line; a line with nothing to go on is null. */
export interface SaleCost {
  readonly saleId: string;
  readonly costCents: number;
  readonly unknownLines: number;
  readonly lines: readonly { readonly lineId: string; readonly costCents: number | null }[];
  readonly costMissing: readonly string[];
}

export interface SaleSummary {
  readonly id: string;
  readonly reference: string;
  readonly soldAt: string;
  readonly saleDate: string;
  readonly deviceCode: string;
  readonly soldBy: string;
  readonly event: string | null;
  readonly location?: string | null;
  /** Retail, tradeshow or TikTok, for the accounts (D, 15 October 2026). */
  readonly channel?: "retail" | "tradeshow" | "tiktok";
  /** Paid partly in cash: that part; the rest was PayNow. */
  readonly cashPartCents?: number | null;
  readonly customerName: string | null;
  readonly totalCents: number;
  readonly status: "unpaid" | "paid" | "void" | "reversed";
  readonly paymentMethod: "cash" | "paynow" | "tiktok" | null;
  readonly paidTo: string | null;
  readonly cancelReason: string | null;
  readonly lines: readonly { lineId?: string; description: string; quantity: number; lineTotalCents: number }[];
  readonly openConflicts: number;
  /** Whether the bank has been seen paying a PayNow sale in. */
  readonly reconciliation: "matched" | "waiting" | "not needed";
  /** Photographs the booth took of something it sold that was not on the system. */
  readonly photoIds: readonly string[];
  /** For those who see costs: what the sale cost, and lines with no cost to go on. */
  readonly costCents?: number;
  readonly costUnknownLines?: number;
  /** Items sold for money with no cost price, or a cost of 0. */
  readonly costMissing?: readonly string[];
  /** Each line's cost by its id; null when it has no cost price. */
  readonly lineCosts?: Readonly<Record<string, number | null>>;
}

export interface PosConflict {
  readonly id: string;
  readonly kind: string;
  readonly detail: string;
  readonly reference: string | null;
  readonly createdAt: string;
}

export interface Buyback {
  readonly id: string;
  readonly reference: string;
  readonly boughtAt: string;
  readonly boughtByName: string;
  readonly sellerName: string;
  readonly sellerPhone: string | null;
  readonly description: string;
  readonly amountCents: number;
  readonly method: "cash" | "paynow" | "later";
  readonly status: "open" | "finished" | "void";
  readonly event: string | null;
  readonly photos: number;
  readonly photoIds: readonly string[];
}

/** One thing that came in on a buyback, as the desk works it out from the photographs. */
export interface BuybackFinishLine {
  readonly productId?: string | null;
  readonly isSlab?: boolean;
  readonly description: string;
  readonly quantity: number;
  readonly costCents: number;
  /** What the label says, read off the photograph, saved with the slab in one go. */
  readonly slab?: SlabInput | null;
}

/** What the model read off a slab's label. Null where it could not, and unsure where it guessed. */
export interface SlabReading {
  /** Where the slab sits in the photo, as fractions; the kept photo is cropped to it. */
  readonly slabBox?: { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number } | null;
  readonly labelText?: string | null;
  /** Where the cert number is in the photo, as fractions. */
  readonly certBox?: { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number } | null;
  readonly yearRegionEra?: string | null;
  readonly certNo: string | null;
  readonly grader: string | null;
  readonly grade: string | null;
  readonly gradeDescription: string | null;
  readonly cardName: string | null;
  readonly setName: string | null;
  readonly yearReleased: number | null;
  readonly cardNumber: string | null;
  readonly language: string | null;
  readonly rarity: string | null;
  readonly unsure: readonly string[];
}

export interface ProductPhotoMatch {
  readonly description: string | null;
  readonly note: string;
  readonly candidates: readonly {
    readonly productId: string;
    readonly confidence: "high" | "medium" | "low";
    readonly reason: string;
  }[];
}

export interface InvoiceReading {
  readonly supplierName: string | null;
  readonly invoiceNo: string | null;
  readonly invoiceDate: string | null;
  readonly currency: string | null;
  readonly total: string | null;
  /** A discount off the whole invoice, when one is printed. */
  readonly discount?: string | null;
  /** Who sent it: address, phone, email, website and registration number as printed. */
  readonly supplier?: SupplierDetails;
  readonly lines: readonly { description: string; quantity: number; unitPrice?: string | null; lineTotal: string | null; productId?: string | null }[];
  readonly unsure: readonly string[];
  readonly warnings: readonly string[];
}

/** One row of a stock list, as the server read it. */
/** A PayNow payment OCBC's alert email reported, and the sale it was for once known. */
export interface PaynowReceipt {
  readonly eventId: string;
  readonly amountCents: number;
  readonly paidAt: string;
  readonly receivedAt: string;
  readonly saleId: string | null;
  readonly saleReference: string | null;
  readonly matchedBy: string | null;
  readonly matchNote: string | null;
  /** Other payments of the same amount came within five minutes: the group, this one included. */
  readonly review: { readonly count: number; readonly firstAt: string; readonly lastAt: string } | null;
}

/** A key a script posts PayNow alerts with. The key itself is only ever seen once. */
export interface PaynowKey {
  readonly id: string;
  readonly label: string;
  readonly endsWith: string;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly lastUsedAt: string | null;
  readonly revokedAt: string | null;
}

/** A sale the booth asked a supervisor about, with what is wrong. */
export interface SaleFlag {
  readonly id: string;
  readonly saleId: string;
  readonly reference: string;
  readonly soldAt: string;
  readonly soldBy: string;
  readonly deviceCode: string;
  readonly totalCents: number;
  readonly saleStatus: string;
  readonly paymentMethod: string | null;
  readonly lines: readonly { description: string; quantity: number; lineTotalCents: number }[];
  readonly note: string;
  readonly askedBy: string;
  readonly askedAt: string;
  readonly status: "open" | "allowed" | "refused" | "done";
  readonly decidedBy: string | null;
  readonly decidedAt: string | null;
  readonly decisionNote: string | null;
}

export interface ImportPlanRow {
  readonly line: number;
  readonly kind: "product" | "slab";
  readonly code: string | null;
  readonly name: string;
  readonly quantity: number;
  readonly totalCostCents: number;
  readonly priceCents: number | null;
  readonly locationName: string | null;
  readonly game: string | null;
  /** Where it goes inside its category on the booth screen, when the sheet says. */
  readonly section?: string | null;
  /** Held and not sold, when the sheet says so either way. */
  readonly notForSale?: boolean | null;
  readonly newProduct: boolean;
  readonly problem: string | null;
}

export interface ImportPlan {
  readonly sheetName: string | null;
  readonly columns: Record<string, string>;
  readonly missing: string[];
  readonly rows: readonly ImportPlanRow[];
  readonly totals: { readonly rows: number; readonly ready: number; readonly problems: number; readonly costCents: number };
}

export interface PersonSummary {
  readonly personId: string;
  readonly fullName: string;
  readonly isStaff: boolean;
  readonly stock: { readonly lots: number; readonly items: number; readonly costCents: number };
  readonly owed: { readonly vouchers: number; readonly amountCents: number };
  readonly paidCents: number;
  readonly accounts: readonly { readonly id: string; readonly name: string; readonly balanceCents: number }[];
  readonly toCheck: number;
}

export interface ToCheck {
  readonly purchases: number;
  readonly lots: number;
  readonly slabsToFinish: number;
  readonly buybacksToTurnIntoStock: number;
}

export interface DayTakings {
  readonly day: string;
  readonly soldCents: number;
  readonly boughtCents: number;
  readonly sales: number;
}

export interface DashboardView {
  readonly today: string;
  readonly takings: { readonly todayCents: number; readonly monthCents: number; readonly unpaidCents: number; readonly unpaidCount: number };
  readonly days: readonly DayTakings[];
  readonly money: { readonly bankCents: number; readonly unmatchedLines: number; readonly owedCents: number; readonly owedCount: number } | null;
  readonly stock: { readonly companyCents: number; readonly companyItems: number; readonly belowZero: number } | null;
  readonly waiting: {
    readonly toCheck: number;
    readonly toFinish: number;
    readonly buybacks: number;
    readonly conflicts: number;
    /** Sales the booth has asked a supervisor about. */
    readonly corrections?: number;
    /** PayNow payments no sale has claimed after half an hour. */
    readonly paynowOpen?: number;
  };
  readonly recent: readonly { readonly at: string; readonly what: string; readonly who: string | null }[];
}

/** What a right password gets back: a session, or the ticket to be asked for a code. */
export type SignInResult =
  | { readonly kind: "session"; readonly session: SessionInfo }
  | { readonly kind: "code"; readonly challenge: string; readonly username: string };

export interface TwoFactorStatus {
  readonly on: boolean;
  readonly halfWay: boolean;
  readonly backupCodesLeft: number;
  readonly since: string | null;
}

export interface CashLine {
  readonly id: string;
  readonly happenedOn: string;
  readonly amountCents: number;
  readonly kind: string;
  readonly description: string;
  readonly reason: string | null;
  readonly who: string | null;
  readonly deviceCode: string | null;
}

export interface TillDay {
  readonly accountId: string;
  readonly accountName: string;
  readonly day: string;
  readonly openingCents: number;
  readonly takenCents: number;
  readonly paidOutCents: number;
  readonly bankedCents: number;
  readonly expectedCents: number;
  readonly lines: readonly CashLine[];
  readonly count: {
    readonly countedCents: number;
    readonly differenceCents: number;
    readonly countedBy: string;
    readonly note: string | null;
  } | null;
}

export interface ReportRow {
  readonly label: string;
  readonly sub: string | null;
  readonly quantity: number;
  readonly soldCents: number;
  readonly costCents: number;
}

export interface SalesReport {
  readonly period: { readonly from: string; readonly to: string };
  readonly sold: {
    readonly salesCount: number;
    readonly soldCents: number;
    readonly costCents: number;
    readonly profitCents: number;
    readonly cashCents: number;
    readonly paynowCents: number;
    readonly tiktokCents: number;
    readonly unpaidCents: number;
  };
  readonly tiktok: { readonly soldCents: number; readonly paidOutCents: number; readonly differenceCents: number } | null;
  readonly bought: { readonly purchasesCents: number; readonly buybacksCents: number };
  readonly byDay: readonly ReportRow[];
  readonly byProduct: readonly ReportRow[];
  readonly byPerson: readonly ReportRow[];
  readonly byEvent: readonly ReportRow[];
  readonly spending: readonly { readonly label: string; readonly amountCents: number; readonly lines: number }[];
}

/** One line of a TikTok order, as the plan read it and matched it. */
export interface PlannedLine {
  readonly row: number;
  readonly skuId: string | null;
  readonly sellerSku: string | null;
  readonly productName: string;
  readonly variation: string | null;
  readonly quantity: number;
  readonly units: number;
  readonly lineTotalCents: number;
  readonly productId: string | null;
  readonly slabLotId: string | null;
  readonly productLabel: string | null;
  readonly matchedBy: "remembered" | "code" | "cert" | "name" | "chosen" | null;
}

export type OrderOutcome = "new" | "already_in" | "to_reverse" | "refunded_after" | "needs_product" | "left_out";

export interface PlannedOrder {
  readonly orderId: string;
  readonly status: string;
  readonly paidAt: string | null;
  readonly saleDay: string | null;
  readonly buyer: string | null;
  readonly lines: readonly PlannedLine[];
  readonly totalCents: number;
  readonly orderAmountCents: number | null;
  readonly refundCents: number;
  readonly outcome: OrderOutcome;
  readonly reason: string | null;
  readonly reference: string | null;
  readonly cancelReason: string | null;
}

export interface UnmappedSku {
  readonly skuId: string | null;
  readonly sellerSku: string | null;
  readonly productName: string;
  readonly variation: string | null;
  readonly orders: number;
  readonly chosenProductId: string | null;
  readonly units: number;
}

export interface SkuChoice {
  readonly skuId: string | null;
  readonly sellerSku: string | null;
  readonly productName: string;
  readonly variation: string | null;
  readonly productId: string;
  readonly units?: number | null;
}

export interface OrderPlan {
  readonly sheetName: string | null;
  readonly columns: Record<string, string>;
  readonly warnings: readonly string[];
  readonly orders: readonly PlannedOrder[];
  readonly unmapped: readonly UnmappedSku[];
  readonly totals: {
    readonly orders: number;
    readonly ready: number;
    readonly readyCents: number;
    readonly alreadyIn: number;
    readonly toReverse: number;
    readonly needProduct: number;
    readonly leftOut: number;
  };
  readonly importedBefore: { readonly on: string; readonly by: string } | null;
  readonly settings: { readonly deviceCode: string | null; readonly accountId: string | null; readonly accountName: string | null };
  readonly fromDay: string | null;
  readonly payouts: readonly TikTokPayout[] | null;
  readonly payoutWarnings: readonly string[];
}

/** One transfer from TikTok to the bank: the orders' revenue less TikTok's fees (D, 2 November 2026). */
export interface TikTokPayout {
  readonly referenceId: string;
  readonly paidOn: string;
  readonly amountCents: number;
  readonly revenueCents: number;
  readonly transactionFeeCents: number;
  readonly commissionCents: number;
  readonly shippingCents: number;
  readonly otherFeesCents: number;
  readonly feesCents: number;
  readonly orderCount: number;
  readonly ordersInBooks: number;
  readonly bankLine: { readonly id: string; readonly date: string; readonly accountName: string } | null;
  readonly problem: string | null;
}

export interface OrderImportResult {
  readonly importId: string | null;
  readonly deviceCode: string;
  readonly sales: number;
  readonly totalCents: number;
  readonly reversed: number;
  readonly alreadyIn: number;
  readonly leftOut: number;
  readonly notes: readonly string[];
  readonly payoutsSaved: number;
  readonly payoutsMatched: number;
  readonly payoutsWaiting: number;
}

/** A login: who can sign in, and what they are allowed to do. */
export interface AppUser {
  readonly id: string;
  readonly personId: string;
  readonly username: string;
  readonly fullName: string;
  readonly jobTitle: string | null;
  readonly role: Role;
  readonly isActive: boolean;
  readonly mustChangePassword: boolean;
  readonly hasTwoFactor: boolean;
  readonly lastLoginAt: string | null;
  readonly lockedUntil: string | null;
}

/** The company's own details, as they appear at the head of an invoice or a voucher. */
export interface Company {
  readonly registeredName: string | null;
  readonly tradingName: string | null;
  readonly uen: string | null;
  readonly address: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly website: string | null;
  readonly paymentDetails: string | null;
  readonly documentNote: string | null;
  readonly hasLogo: boolean;
  /** The people who sell may void and correct sales at the booth without a supervisor. */
  readonly staffPutRight: boolean;
  readonly updatedAt: string | null;
  readonly updatedByName: string | null;
  readonly missing: readonly string[];
}

export type CompanyInput = Omit<Company, "hasLogo" | "staffPutRight" | "updatedAt" | "updatedByName" | "missing">;

export interface Claim {
  readonly id: string;
  readonly number: string;
  readonly claimedBy: string;
  readonly claimedByName: string;
  readonly spentOn: string;
  readonly amountCents: number;
  readonly description: string;
  readonly categoryName: string | null;
  readonly note: string | null;
  readonly status: "open" | "approved" | "refused" | "paid" | "withdrawn";
  readonly decidedByName: string | null;
  readonly decidedAt: string | null;
  readonly decisionReason: string | null;
  readonly voucherNumber: string | null;
  readonly photoIds: readonly string[];
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const request: RequestInit = { credentials: "same-origin", ...init };
  if (init?.body) {
    request.headers = { "Content-Type": "application/json", ...init.headers };
  }

  let response: Response;
  try {
    response = await fetch(path, request);
  } catch {
    throw new ApiError("Cannot reach the server. Check the connection and try again.", 0);
  }

  const text = await response.text();
  let body: Record<string, unknown> = {};
  try {
    body = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  } catch {
    // Not our server's answer: a proxy or Cloudflare page, from a connection that dropped
    // on the way. The request may or may not have arrived, so say to look before retrying.
    throw new ApiError(
      `The connection dropped before the server answered (${response.status}). ` +
        "Check whether it was saved before trying again.",
      response.status,
    );
  }

  if (!response.ok) {
    const message = typeof body["error"] === "string" ? body["error"] : "Something went wrong";
    throw new ApiError(message, response.status);
  }

  return body as T;
}

let readingStatus: Promise<boolean> | undefined;

export const api = {
  me: (): Promise<SessionInfo> => call<SessionInfo>("/api/auth/me"),

  /**
   * Sign in.
   *
   * No device is sent: the server works that out from the device's own token. A letter
   * the caller could choose would be a letter two offline iPads could both claim, and
   * that letter goes inside every reference number.
   */
  signIn: async (username: string, password: string): Promise<SignInResult> => {
    const answer = await call<SessionInfo & { codeNeeded?: boolean; challenge?: string; username?: string }>(
      "/api/auth/login",
      { method: "POST", body: JSON.stringify({ username, password }) },
    );
    return answer.codeNeeded && answer.challenge
      ? { kind: "code", challenge: answer.challenge, username: answer.username ?? username }
      : { kind: "session", session: answer };
  },

  /** The second step: the six digits from the app, or one of the backup codes. */
  signInWithCode: (challenge: string, code: string): Promise<SessionInfo> =>
    call<SessionInfo>("/api/auth/2fa", { method: "POST", body: JSON.stringify({ challenge, code }) }),

  twoFactor: (): Promise<{ twoFactor: TwoFactorStatus }> => call("/api/auth/2fa"),

  startTwoFactor: (password: string): Promise<{ secret: string; url: string }> =>
    call("/api/auth/2fa/start", { method: "POST", body: JSON.stringify({ password }) }),

  confirmTwoFactor: (code: string): Promise<{ backupCodes: string[] }> =>
    call("/api/auth/2fa/confirm", { method: "POST", body: JSON.stringify({ code }) }),

  stopTwoFactor: (password: string, code: string): Promise<{ ok: true }> =>
    call("/api/auth/2fa/off", { method: "POST", body: JSON.stringify({ password, code }) }),

  newBackupCodes: (password: string): Promise<{ backupCodes: string[] }> =>
    call("/api/auth/2fa/backup-codes", { method: "POST", body: JSON.stringify({ password }) }),

  signOut: (): Promise<{ ok: true }> => call("/api/auth/logout", { method: "POST" }),

  changePassword: (currentPassword: string, newPassword: string): Promise<{ ok: true }> =>
    call("/api/auth/password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  progress: (): Promise<Progress> => call<Progress>("/api/progress"),

  accounts: (): Promise<{ accounts: Account[] }> => call("/api/accounts"),

  addAccount: (input: NewAccountInput): Promise<{ account: Account }> =>
    call("/api/accounts", { method: "POST", body: JSON.stringify(input) }),

  contacts: (): Promise<{ contacts: Contact[] }> => call("/api/contacts"),

  addContact: (input: NewContactInput): Promise<{ contact: Contact }> =>
    call("/api/contacts", { method: "POST", body: JSON.stringify(input) }),

  people: (): Promise<{ people: Person[] }> => call("/api/people"),

  productBatches: (id: string): Promise<{ batches: ProductBatch[] }> => call(`/api/products/${id}/batches`),

  products: (search?: string): Promise<{ products: Product[] }> =>
    call(`/api/products${search ? `?search=${encodeURIComponent(search)}` : ""}`),

  addProduct: (input: {
    /** Left empty, one is made from the name. */
    code?: string | null;
    name: string;
    category: string;
    game?: string | null;
    setName?: string | null;
    language?: string | null;
    itemType: ItemType;
    packsPerBox?: number | null;
    defaultPrice?: string | null;
    tracksStock?: boolean;
    section?: string | null;
    notForSale?: boolean;
  }): Promise<{ product: Product }> =>
    call("/api/products", { method: "POST", body: JSON.stringify(input) }),

  /** A draft description from the reading. Nothing is saved until setProductDescription. */
  describeProduct: (id: string): Promise<{ description: string }> => call(`/api/products/${id}/describe`, { method: "POST" }),

  setProductDescription: (id: string, description: string | null): Promise<{ ok: true }> =>
    call(`/api/products/${id}/description`, { method: "POST", body: JSON.stringify({ description }) }),

  /** One payment out that was for several things, a line for each under its own category. */
  splitBankLine: (
    id: string,
    input: { payeeName?: string | null; note?: string | null; parts: { categoryId: string; description: string; amountCents: number }[] },
  ): Promise<{ ok: true; purchaseNumber: string; voucherNumber: string | null }> =>
    call(`/api/bank/lines/${id}/split`, { method: "POST", body: JSON.stringify(input) }),

  /** Transfers out from the bank's alerts still to explain, or a day's, done or not. */
  /** A flag for review put on or taken off. */
  setFlag: (kind: "bank_line" | "purchase" | "stock_lot", id: string, flagged: boolean, note?: string): Promise<{ ok: true }> =>
    call("/api/flags", { method: "POST", body: JSON.stringify({ kind, id, flagged, ...(note ? { note } : {}) }) }),

  outgoingTransfers: (filter: { pending?: boolean; day?: string } = {}): Promise<{ transfers: import("./OutgoingTransfers").OutgoingTransfer[] }> => {
    const params = new URLSearchParams();
    if (filter.pending === false) params.set("pending", "0");
    if (filter.day) params.set("day", filter.day);
    const query = params.toString();
    return call(`/api/paynow/outgoing${query ? `?${query}` : ""}`);
  },

  /** Every transfer the alerts reported, in and out, with what each was for (Daren, 19 October 2026). */
  alertTransfers: (filter: { waiting?: boolean; from?: string; to?: string } = {}): Promise<{ transfers: import("./AlertLog").AlertTransfer[] }> => {
    const params = new URLSearchParams();
    if (filter.waiting) params.set("waiting", "1");
    if (filter.from) params.set("from", filter.from);
    if (filter.to) params.set("to", filter.to);
    const query = params.toString();
    return call(`/api/paynow/transfers${query ? `?${query}` : ""}`);
  },

  combineOutgoing: (ids: readonly string[], amountCents: number): Promise<{ message: string }> =>
    call("/api/paynow/outgoing/combine", { method: "POST", body: JSON.stringify({ ids, amountCents }) }),

  describeReceipt: (id: string, description: string): Promise<{ ok: true }> =>
    call(`/api/paynow/receipts/${encodeURIComponent(id)}/describe`, { method: "POST", body: JSON.stringify({ description }) }),

  describeOutgoing: (id: string, description: string, settled: boolean): Promise<{ ok: true }> =>
    call(`/api/paynow/outgoing/${encodeURIComponent(id)}`, { method: "POST", body: JSON.stringify({ description, settled }) }),

  setProductType: (id: string, itemType: ItemType): Promise<{ ok: true }> =>
    call(`/api/products/${id}/type`, { method: "POST", body: JSON.stringify({ itemType }) }),

  setProductContents: (id: string, containsProductId: string | null, containsQuantity: number | null): Promise<{ ok: true }> =>
    call(`/api/products/${id}/contents`, { method: "POST", body: JSON.stringify({ containsProductId, containsQuantity }) }),

  locations: (): Promise<{ locations: Location[] }> => call("/api/locations"),

  renameLocation: (id: string, name: string, kind?: Location["kind"]): Promise<{ location: Location }> =>
    call(`/api/locations/${id}`, { method: "POST", body: JSON.stringify({ name, ...(kind ? { kind } : {}) }) }),

  addLocation: (name: string, kind: Location["kind"]): Promise<{ location: Location }> =>
    call("/api/locations", { method: "POST", body: JSON.stringify({ name, kind }) }),

  stock: (filter: {
    search?: string;
    owner?: OwnerKind | "";
    locationId?: string;
    toFinish?: boolean;
    toCheck?: boolean;
  }): Promise<StockView> => {
    const params = new URLSearchParams();
    if (filter.toFinish) params.set("toFinish", "true");
    if (filter.toCheck) params.set("toCheck", "true");
    if (filter.search) params.set("search", filter.search);
    if (filter.owner) params.set("owner", filter.owner);
    if (filter.locationId) params.set("locationId", filter.locationId);
    const query = params.toString();
    return call(`/api/stock${query ? `?${query}` : ""}`);
  },

  lot: (id: string): Promise<{ lot: Lot; movements: Movement[] }> => call(`/api/stock/${id}`),

  moveLot: (id: string, input: { quantity: number; toLocationId: string; happenedOn: string; reason?: string }) =>
    call<{ from: Lot; to: Lot }>(`/api/stock/${id}/transfer`, { method: "POST", body: JSON.stringify(input) }),

  openBoxes: (id: string, input: { boxes: number; packProductId: string; happenedOn: string }) =>
    call<{ box: Lot; packs: Lot }>(`/api/stock/${id}/open`, { method: "POST", body: JSON.stringify(input) }),

  writeOff: (id: string, input: { quantity: number; happenedOn: string; reason: string }) =>
    call<{ lot: Lot }>(`/api/stock/${id}/write-off`, { method: "POST", body: JSON.stringify(input) }),

  slab: (id: string): Promise<{ slab: SlabDetails | null }> => call(`/api/stock/${id}/slab`),

  saveSlab: (id: string, input: SlabInput): Promise<{ slab: SlabDetails }> =>
    call(`/api/stock/${id}/slab`, { method: "POST", body: JSON.stringify(input) }),

  sellToCompany: (input: {
    lotId: string;
    quantity: number;
    issuedOn: string;
    paidFromAccountId: string;
    locationId?: string | null;
  }): Promise<{ voucherNumber: string; companyLotId: string; amountCents: number }> =>
    call("/api/purchases/from-person", { method: "POST", body: JSON.stringify(input) }),

  updateProduct: (
    id: string,
    input: {
      name: string;
      category: string;
      game?: string | null;
      setName?: string | null;
      language?: string | null;
      packsPerBox?: number | null;
      section?: string | null;
      notForSale?: boolean;
      tracksStock?: boolean;
    },
  ): Promise<{ ok: true }> => call(`/api/products/${id}`, { method: "POST", body: JSON.stringify(input) }),

  setProductActive: (id: string, active: boolean): Promise<{ ok: true }> =>
    call(`/api/products/${id}/active`, { method: "POST", body: JSON.stringify({ active }) }),

  productHistory: (id: string): Promise<{ entries: { id: string; at: string; action: string; actorName: string | null; before: Record<string, unknown> | null; after: Record<string, unknown> | null; reason: string | null; canUndo: boolean }[] }> => call(`/api/products/${id}/history`),
  undoProductChange: (id: string, entryId: string, reason: string): Promise<{ ok: true }> => call(`/api/products/${id}/undo`, { method: "POST", body: JSON.stringify({ entryId, reason }) }),

  pinProduct: (id: string, pinned: boolean): Promise<{ ok: true }> =>
    call(`/api/products/${id}/pin`, { method: "POST", body: JSON.stringify({ pinned }) }),

  setProductPicture: (id: string, contentType: string, data: string): Promise<{ photoId: string }> =>
    call(`/api/products/${id}/picture`, { method: "POST", body: JSON.stringify({ contentType, data }) }),

  findProductPictures: (id: string, query?: string): Promise<{ pictures: PictureSuggestion[] }> => call(`/api/products/${id}/find-pictures`, { method: "POST", ...(query ? { body: JSON.stringify({ query }) } : {}) }),
  selectProductPicture: (id: string, token: string): Promise<{ contentType: string; data: string }> => call(`/api/products/${id}/select-picture`, { method: "POST", body: JSON.stringify({ token }) }),

  setProductPrice: (id: string, price: string): Promise<{ product: Product }> =>
    call(`/api/products/${id}/price`, { method: "POST", body: JSON.stringify({ price }) }),

  countLot: (id: string, countedQuantity: number, happenedOn: string, reason: string): Promise<{ lot: Lot }> =>
    call(`/api/stock/${id}/count`, {
      method: "POST",
      body: JSON.stringify({ countedQuantity, happenedOn, reason }),
    }),

  purchases: (): Promise<{ purchases: Purchase[] }> => call("/api/purchases"),

  addPurchase: (input: NewPurchaseInput): Promise<{ purchase: Purchase }> =>
    call("/api/purchases", { method: "POST", body: JSON.stringify(input) }),

  replacePurchase: (id: string, input: NewPurchaseInput): Promise<{ purchase: Purchase }> =>
    call(`/api/purchases/${id}/replace`, { method: "POST", body: JSON.stringify(input) }),

  cancelPurchase: (id: string, reason: string): Promise<{ ok: true }> =>
    call(`/api/purchases/${id}/cancel`, { method: "POST", body: JSON.stringify({ reason }) }),

  attachToPurchase: (id: string, file: { contentType: string; data: string }): Promise<{ attachment: { id: string } }> =>
    call(`/api/purchases/${id}/attachments`, {
      method: "POST",
      body: JSON.stringify({ photoId: `a${Date.now()}${Math.floor(Math.random() * 1000)}`, contentType: file.contentType, data: file.data }),
    }),

  removePurchaseAttachment: (id: string, photoId: string): Promise<{ ok: true }> =>
    call(`/api/purchases/${id}/attachments/${photoId}/remove`, { method: "POST" }),

  verifyPurchase: (id: string): Promise<{ ok: true }> =>
    call(`/api/purchases/${id}/verify`, { method: "POST" }),

  vouchers: (): Promise<{ vouchers: Voucher[] }> => call("/api/vouchers"),

  verifyVoucher: (id: string): Promise<{ ok: true }> => call(`/api/vouchers/${id}/verify`, { method: "POST" }),
  undoVoucherPayment: (id: string, reason: string): Promise<{ ok: true }> =>
    call(`/api/vouchers/${id}/undo-payment`, { method: "POST", body: JSON.stringify({ reason }) }),

  correctVoucherDates: (id: string, input: { issuedOn: string; paidOn?: string | null; purchaseDate?: string; reason: string }): Promise<{ ok: true }> =>
    call(`/api/vouchers/${id}/dates`, { method: "POST", body: JSON.stringify(input) }),

  markVoucherPaid: (id: string, paidOn: string): Promise<{ ok: true }> =>
    call(`/api/vouchers/${id}/paid`, { method: "POST", body: JSON.stringify({ paidOn }) }),

  aiKey: (): Promise<{ key: AiKeyState }> => call("/api/ai/key"),

  setAiKey: (key: string | null): Promise<{ key: AiKeyState }> =>
    call("/api/ai/key", { method: "POST", body: JSON.stringify({ key }) }),

  setSlabPhoto: (lotId: string, file: { contentType: string; data: string }): Promise<{ photo: { id: string } }> =>
    call(`/api/stock/${lotId}/photo`, {
      method: "POST",
      body: JSON.stringify({ photoId: `s${Date.now()}${Math.floor(Math.random() * 1000)}`, contentType: file.contentType, data: file.data }),
    }),

  saleCosts: (ids: readonly string[]): Promise<{ costs: SaleCost[] }> =>
    call(`/api/sales/costs?ids=${encodeURIComponent(ids.join(","))}`),

  deleteVoucher: (id: string, reason: string): Promise<{ ok: true }> =>
    call(`/api/vouchers/${id}/delete`, { method: "POST", body: JSON.stringify({ reason }) }),

  cancelVoucher: (id: string, reason: string): Promise<{ ok: true }> =>
    call(`/api/vouchers/${id}/cancel`, { method: "POST", body: JSON.stringify({ reason }) }),

  thisDevice: (): Promise<{ device: ThisDevice | null }> => call("/api/devices/this"),

  claimDevice: (code: string): Promise<{ device: ThisDevice }> =>
    call("/api/devices/claim", { method: "POST", body: JSON.stringify({ code }) }),

  devices: (): Promise<{ devices: Device[]; nextFreeCode: string | null }> => call("/api/devices"),

  registerDevice: (
    name: string,
    kind: DeviceKind,
    code?: string,
  ): Promise<{ device: Device; claimCode: string }> =>
    call("/api/devices", {
      method: "POST",
      body: JSON.stringify(code ? { name, kind, code } : { name, kind }),
    }),

  setDeviceAccounts: (id: string, cashAccountId: string | null, paynowAccountId: string | null): Promise<{ ok: true }> =>
    call(`/api/devices/${id}/accounts`, { method: "POST", body: JSON.stringify({ cashAccountId, paynowAccountId }) }),

  reissueClaim: (id: string): Promise<{ claimCode: string }> =>
    call(`/api/devices/${id}/reissue`, { method: "POST" }),

  renameDevice: (id: string, name: string, kind: DeviceKind): Promise<{ ok: true }> =>
    call(`/api/devices/${id}`, { method: "POST", body: JSON.stringify({ name, kind }) }),

  deleteDevice: (id: string): Promise<{ ok: true }> => call(`/api/devices/${id}/delete`, { method: "POST" }),

  retireDevice: (id: string, reason: string): Promise<{ ok: true }> =>
    call(`/api/devices/${id}/retire`, { method: "POST", body: JSON.stringify({ reason }) }),

  balances: (): Promise<BalanceSummary> => call("/api/balances"),

  previewStatement: (file: StatementFile): Promise<{ preview: ImportPreview }> =>
    call("/api/bank/imports/preview", { method: "POST", body: JSON.stringify(file) }),

  importStatement: (file: StatementFile): Promise<{ result: ImportResult }> =>
    call("/api/bank/imports", { method: "POST", body: JSON.stringify(file) }),

  bankImports: (): Promise<{ imports: BankImport[] }> => call("/api/bank/imports"),

  bankLine: (id: string): Promise<{ line: BankLineDetail }> => call(`/api/bank/lines/${id}`),

  matchBankLine: (
    id: string,
    input: { kind: MatchKind; targetId: string; note?: string | null; alsoSameCounterparty?: boolean; changeInCash?: boolean; tradeInVoucherId?: string; overpaid?: boolean; inParts?: boolean },
  ): Promise<{ result: { matched: number; skippedClosed: number } }> =>
    call(`/api/bank/lines/${id}/match`, { method: "POST", body: JSON.stringify(input) }),

  attachToBankLine: (id: string, file: { contentType: string; data: string }): Promise<{ attachment: { id: string } }> =>
    call(`/api/bank/lines/${id}/attachments`, {
      method: "POST",
      body: JSON.stringify({ photoId: `a${Date.now()}${Math.floor(Math.random() * 1000)}`, contentType: file.contentType, data: file.data }),
    }),

  removeBankLineAttachment: (id: string, photoId: string): Promise<{ ok: true }> =>
    call(`/api/bank/lines/${id}/attachments/${photoId}/remove`, { method: "POST" }),

  unmatchBankLine: (id: string, reason: string): Promise<{ ok: true }> =>
    call(`/api/bank/lines/${id}/unmatch`, { method: "POST", body: JSON.stringify({ reason }) }),

  setBankLineCategory: (id: string, categoryId: string | null): Promise<{ ok: true }> =>
    call(`/api/bank/lines/${id}/category`, { method: "POST", body: JSON.stringify({ categoryId }) }),

  ignoreBankLine: (id: string, reason: string): Promise<{ ok: true }> =>
    call(`/api/bank/lines/${id}/ignore`, { method: "POST", body: JSON.stringify({ reason }) }),

  /** Lines that came in twice from exports that dated them differently (D, 16 October 2026). */
  bankDuplicates: (accountId: string): Promise<{ groups: DuplicateGroup[] }> => call(`/api/bank/duplicates?accountId=${encodeURIComponent(accountId)}`),

  setAsideDuplicates: (accountId: string): Promise<{ setAside: number }> =>
    call("/api/bank/duplicates/set-aside", { method: "POST", body: JSON.stringify({ accountId }) }),

  bankLines: (filter: {
    accountId?: string;
    search?: string;
    direction?: "in" | "out" | "";
    unmatched?: boolean;
    aside?: boolean;
    from?: string;
    to?: string;
    page?: number;
  }): Promise<BankLinePage> => {
    const params = new URLSearchParams();
    if (filter.accountId) params.set("accountId", filter.accountId);
    if (filter.search) params.set("search", filter.search);
    if (filter.direction) params.set("direction", filter.direction);
    if (filter.unmatched) params.set("unmatched", "1");
    if (filter.aside) params.set("aside", "1");
    if (filter.from) params.set("from", filter.from);
    if (filter.to) params.set("to", filter.to);
    if (filter.page && filter.page > 1) params.set("page", String(filter.page));
    const query = params.toString();
    return call(`/api/bank/lines${query ? `?${query}` : ""}`);
  },

  /** Everyone who can sell or has sold, for choosing whose sales go into a file. */
  salesSellers: (): Promise<{ sellers: { id: string; name: string }[] }> => call("/api/sales/sellers"),

  /** Where the file of some days' sales is fetched from, one row per item. */
  /** The bank lines with what each was matched to, as an Excel file. */
  bankFileUrl: (from: string, to: string, accountId?: string): string => {
    const params = new URLSearchParams({ from, to });
    if (accountId) params.set("accountId", accountId);
    return `/api/bank/export?${params.toString()}`;
  },

  salesFileUrl: (from: string, to: string, sellerId?: string): string => {
    const params = new URLSearchParams({ from, to });
    if (sellerId) params.set("seller", sellerId);
    return `/api/sales/export?${params.toString()}`;
  },

  sales: (filter: { day?: string; status?: string } = {}): Promise<{ sales: SaleSummary[] }> => {
    const params = new URLSearchParams();
    if (filter.day) params.set("day", filter.day);
    if (filter.status) params.set("status", filter.status);
    const query = params.toString();
    return call(`/api/sales${query ? `?${query}` : ""}`);
  },

  reverseSale: (id: string, reason: string): Promise<{ ok: true }> =>
    call(`/api/sales/${id}/reverse`, { method: "POST", body: JSON.stringify({ reason }) }),

  /** The prices on a sale put right (D, 29 October 2026). */
  changeSaleAmounts: (id: string, lines: readonly { lineId: string; unitPriceCents: number }[], reason: string): Promise<{ message: string }> =>
    call(`/api/sales/${id}/amounts`, { method: "POST", body: JSON.stringify({ lines, reason }) }),

  /** A reversal undone, for one clicked by mistake (D, 18 October 2026). */
  undoReversal: (id: string): Promise<{ message: string }> => call(`/api/sales/${id}/unreverse`, { method: "POST" }),

  /** A cash sale and a PayNow sale of one thing, made one sale paid in both (D, 18 October 2026). */
  combineSales: (keepId: string, otherId: string): Promise<{ message: string }> =>
    call(`/api/sales/${keepId}/combine`, { method: "POST", body: JSON.stringify({ otherId }) }),

  /** A sale keyed in wrong, taken off on its own day (D, 15 October 2026). */
  removeSale: (id: string, reason: string): Promise<{ ok: true }> =>
    call(`/api/sales/${id}/remove`, { method: "POST", body: JSON.stringify({ reason }) }),

  posConflicts: (): Promise<{ conflicts: PosConflict[] }> => call("/api/pos/conflicts"),

  /** One sale's photographs, for the link in the sales file. */
  salePhotos: (reference: string): Promise<{ reference: string; day: string; photoIds: string[] }> =>
    call(`/api/sales/photos/${encodeURIComponent(reference)}`),

  paynowReceipts: (filter: { day?: string; minutes?: number; open?: boolean } = {}): Promise<{ receipts: PaynowReceipt[] }> => {
    const params = new URLSearchParams();
    if (filter.day) params.set("day", filter.day);
    if (filter.minutes) params.set("minutes", String(filter.minutes));
    if (filter.open) params.set("open", "1");
    return call(`/api/paynow/receipts?${params.toString()}`);
  },

  paynowCandidates: (eventId: string): Promise<{ sales: { id: string; reference: string; paidAt: string }[] }> =>
    call(`/api/paynow/receipts/${encodeURIComponent(eventId)}/candidates`),

  matchPaynow: (eventId: string, saleId: string | null): Promise<{ ok: true }> =>
    call(`/api/paynow/receipts/${encodeURIComponent(eventId)}/match`, { method: "POST", body: JSON.stringify({ saleId }) }),

  paynowKeys: (): Promise<{ keys: PaynowKey[] }> => call("/api/paynow/keys"),

  makePaynowKey: (label: string): Promise<{ key: string; apiKey: PaynowKey }> =>
    call("/api/paynow/keys", { method: "POST", body: JSON.stringify({ label }) }),

  revokePaynowKey: (id: string): Promise<{ ok: true }> => call(`/api/paynow/keys/${id}/revoke`, { method: "POST" }),

  /** The booth's questions about its sales, for a supervisor. */
  saleFlags: (): Promise<{ flags: SaleFlag[] }> => call("/api/sale-flags"),

  decideSaleFlag: (id: string, allow: boolean, note?: string): Promise<{ ok: true }> =>
    call(`/api/sale-flags/${id}/decide`, { method: "POST", body: JSON.stringify({ allow, note: note ?? null }) }),

  buybacks: (filter: { day?: string; status?: string } = {}): Promise<{ buybacks: Buyback[] }> => {
    const params = new URLSearchParams();
    if (filter.day) params.set("day", filter.day);
    if (filter.status) params.set("status", filter.status);
    const query = params.toString();
    return call(`/api/buybacks${query ? `?${query}` : ""}`);
  },

  finishBuyback: (id: string, input: { locationId?: string | null; lines: BuybackFinishLine[] }): Promise<{ lots: number }> =>
    call(`/api/buybacks/${id}/finish`, { method: "POST", body: JSON.stringify(input) }),

  voidBuyback: (id: string, reason: string): Promise<{ ok: true }> =>
    call(`/api/buybacks/${id}/void`, { method: "POST", body: JSON.stringify({ reason }) }),

  moneyLines: (filter: { account?: string; from?: string; to?: string; direction?: "in" | "out" | ""; page?: number }): Promise<MoneyLines> =>
    call(`/api/money/lines?${moneyQuery(filter)}`),

  moneyExportUrl: (filter: { account?: string; from?: string; to?: string; direction?: "in" | "out" | "" }): string =>
    `/api/money/export?${moneyQuery(filter)}`,

  recordEarlierSale: (input: {
    day: string;
    time?: string | null;
    lines: { productId?: string | null; slabLotId?: string | null; quantity: number; unitPriceCents: number; note?: string | null }[];
    discountCents?: number;
    surchargeCents?: number;
    method: "cash" | "paynow";
    cashPartCents?: number;
    cashAccountId?: string | null;
    accountId: string;
    locationId?: string | null;
    event?: string | null;
    customerName?: string | null;
    bankLineId?: string | null;
  }): Promise<{ ok: true; saleId: string; reference: string }> =>
    call("/api/sales/earlier", { method: "POST", body: JSON.stringify(input) }),

  addSalePhoto: (saleId: string, file: { contentType: string; data: string }): Promise<{ photo: { id: string } }> =>
    call(`/api/sales/${saleId}/photos`, { method: "POST", body: JSON.stringify(file) }),

  relocateSales: (input: {
    day: string;
    deviceCode: string;
    fromLocationId: string | null;
    toLocationId: string;
    event?: string | null;
    reason: string;
  }): Promise<{ ok: true; sales: number; buybacks: number; to: string }> =>
    call("/api/sales/relocate", { method: "POST", body: JSON.stringify(input) }),

  changeBuybackPayment: (id: string, method: "cash" | "paynow", reason: string): Promise<{ ok: true; accountName: string }> =>
    call(`/api/buybacks/${id}/payment`, { method: "POST", body: JSON.stringify({ method, reason }) }),

  receiveStock: (input: {
    productId: string;
    quantity: number;
    unitCost?: string | null;
    acquiredOn?: string;
    locationId?: string | null;
    notes?: string | null;
  }): Promise<{ lot: Lot }> => call("/api/stock/receive", { method: "POST", body: JSON.stringify(input) }),

  previewStockImport: (fileName: string, content: string, uncounted?: boolean): Promise<{ plan: ImportPlan }> =>
    call("/api/stock/import/preview", { method: "POST", body: JSON.stringify({ fileName, content, uncounted }) }),

  importStock: (input: {
    fileName: string;
    content: string;
    onDate: string;
    locationId?: string | null;
    acquisition?: "opening_balance" | "purchase" | "gift";
    skipProblems?: boolean;
    /** A price list rather than a count: nothing in it becomes counted stock. */
    uncounted?: boolean;
  }): Promise<{
    result: { lots: number; productsCreated: number; productsUpdated: number; skipped: number; costCents: number };
  }> =>
    call("/api/stock/import", { method: "POST", body: JSON.stringify(input) }),

  dashboard: (): Promise<{ dashboard: DashboardView }> => call("/api/dashboard"),

  claims: (): Promise<{ claims: Claim[]; canApprove: boolean }> => call("/api/claims"),

  addClaim: (input: {
    spentOn: string;
    amount: string;
    description: string;
    categoryId?: string | null;
    note?: string | null;
  }): Promise<{ claim: Claim }> => call("/api/claims", { method: "POST", body: JSON.stringify(input) }),

  addReceipt: (id: string, contentType: string, data: string): Promise<{ photo: { id: string } }> =>
    call(`/api/claims/${id}/receipt`, {
      method: "POST",
      body: JSON.stringify({ photoId: `r${Date.now()}${Math.floor(Math.random() * 1000)}`, contentType, data }),
    }),

  approveClaim: (id: string, paidFromAccountId: string, note: string | null): Promise<{ claim: Claim }> =>
    call(`/api/claims/${id}/approve`, { method: "POST", body: JSON.stringify({ paidFromAccountId, note }) }),

  refuseClaim: (id: string, reason: string): Promise<{ claim: Claim }> =>
    call(`/api/claims/${id}/refuse`, { method: "POST", body: JSON.stringify({ reason }) }),

  withdrawClaim: (id: string): Promise<{ ok: true }> => call(`/api/claims/${id}/withdraw`, { method: "POST" }),

  users: (): Promise<{ users: AppUser[]; peopleWithoutLogin: { id: string; fullName: string }[]; roles: Role[] }> =>
    call("/api/users"),

  addUser: (input: {
    fullName: string;
    username: string;
    role: Role;
    jobTitle?: string | null;
    personId?: string | null;
  }): Promise<{ user: AppUser; temporaryPassword: string }> =>
    call("/api/users", { method: "POST", body: JSON.stringify(input) }),

  setUserRole: (id: string, role: Role): Promise<{ user: AppUser }> =>
    call(`/api/users/${id}/role`, { method: "POST", body: JSON.stringify({ role }) }),

  setUserActive: (id: string, active: boolean): Promise<{ user: AppUser }> =>
    call(`/api/users/${id}/active`, { method: "POST", body: JSON.stringify({ active }) }),

  resetUserPassword: (id: string): Promise<{ user: AppUser; temporaryPassword: string }> =>
    call(`/api/users/${id}/password`, { method: "POST" }),

  /** Whether reading is switched on. Asked once, because it does not change while the page is open. */
  readingAvailable: (): Promise<boolean> => {
    readingStatus ??= call<{ available: boolean }>("/api/ai/status")
      .then((result) => result.available)
      .catch(() => false);
    return readingStatus;
  },

  readSlab: (input: { photoId: string } | { contentType: string; data: string }): Promise<{ reading: SlabReading }> =>
    call("/api/ai/read-slab", { method: "POST", body: JSON.stringify(input) }),

  matchProductPhoto: (input: { contentType: string; data: string }): Promise<{ match: ProductPhotoMatch }> =>
    call("/api/ai/match-product-photo", { method: "POST", body: JSON.stringify(input) }),

  readInvoice: (input: { contentType: string; fileName?: string; data: string }): Promise<{ reading: InvoiceReading }> =>
    call("/api/ai/read-invoice", { method: "POST", body: JSON.stringify(input) }),

  company: (): Promise<{ company: Company }> => call("/api/company"),

  saveCompany: (input: CompanyInput): Promise<{ company: Company }> =>
    call("/api/company", { method: "PUT", body: JSON.stringify(input) }),

  saveLogo: (contentType: string, data: string): Promise<{ company: Company }> =>
    call("/api/company/logo", { method: "PUT", body: JSON.stringify({ contentType, data }) }),

  removeLogo: (): Promise<{ company: Company }> => call("/api/company/logo", { method: "DELETE" }),

  setStaffPutRight: (on: boolean): Promise<{ company: Company }> =>
    call("/api/company/staff-put-right", { method: "PUT", body: JSON.stringify({ on }) }),

  previewTikTokOrders: (input: {
    fileName: string;
    content: string;
    choices?: SkuChoice[];
    fromDay?: string | null;
    payoutFile?: { fileName: string; content: string } | null;
  }): Promise<{ plan: OrderPlan }> =>
    call("/api/sales/tiktok/preview", { method: "POST", body: JSON.stringify(input) }),

  suggestTikTokListings: (
    listings: { key: string; productName: string; variation: string | null }[],
  ): Promise<{ suggestions: { key: string; productId: string; by: "ai" | "words" }[]; usedAi: boolean; aiProblem: string | null }> =>
    call("/api/sales/tiktok/suggest", { method: "POST", body: JSON.stringify({ listings }) }),

  importTikTokOrders: (input: {
    fileName: string;
    content: string;
    accountId: string;
    locationId?: string | null;
    choices?: SkuChoice[];
    reverseCancelled?: boolean;
    fromDay?: string | null;
    payoutFile?: { fileName: string; content: string } | null;
  }): Promise<{ result: OrderImportResult }> =>
    call("/api/sales/tiktok/import", { method: "POST", body: JSON.stringify(input) }),

  salesReport: (from: string, to: string): Promise<{ report: SalesReport }> =>
    call(`/api/reports/sales?from=${from}&to=${to}`),

  till: (accountId: string, day: string): Promise<{ till: TillDay }> =>
    call(`/api/cash/till?accountId=${encodeURIComponent(accountId)}&day=${day}`),

  countTill: (
    accountId: string,
    day: string,
    countedCents: number,
    note: string | null,
  ): Promise<{ result: { expectedCents: number; differenceCents: number } }> =>
    call("/api/cash/count", { method: "POST", body: JSON.stringify({ accountId, day, countedCents, note }) }),

  bankCash: (accountId: string, day: string, amountCents: number): Promise<{ ok: true }> =>
    call("/api/cash/banked", { method: "POST", body: JSON.stringify({ accountId, day, amountCents }) }),

  peopleSummary: (): Promise<{ people: PersonSummary[]; canSeeAll: boolean }> => call("/api/people/summary"),

  checks: (): Promise<{ waiting: ToCheck }> => call("/api/checks"),

  verifyLot: (id: string): Promise<{ ok: true }> => call(`/api/stock/${id}/verify`, { method: "POST" }),

  categories: (): Promise<{ categories: Category[] }> => call("/api/categories"),

  categoryEntries: (id: string): Promise<{ entries: CategoryEntry[] }> => call(`/api/categories/${id}/entries`),

  /**
   * Add a category.
   *
   * Comes back with suggestions instead of a category when the name looks like one that
   * already exists. Sending it again with evenIfSimilar creates it anyway.
   */
  changeCategory: (
    id: string,
    changes: { name?: string; direction?: Direction; isActive?: boolean; groupId?: string | null; productCategory?: string | null },
  ): Promise<{ category: Category }> =>
    call(`/api/categories/${id}`, { method: "POST", body: JSON.stringify(changes) }),

  deleteCategory: (id: string): Promise<{ ok: true }> => call(`/api/categories/${id}/delete`, { method: "POST" }),

  addCategory: (
    name: string,
    direction: Direction,
    evenIfSimilar = false,
    groupId: string | null = null,
  ): Promise<{ category?: Category; suggestions?: CategorySuggestion[] }> =>
    call("/api/categories", {
      method: "POST",
      body: JSON.stringify({ name, direction, evenIfSimilar, ...(groupId ? { groupId } : {}) }),
    }),

  categoryGroups: (): Promise<{ groups: CategoryGroup[] }> => call("/api/category-groups"),

  addCategoryGroup: (name: string, direction: Direction, forResale: boolean): Promise<{ group: CategoryGroup }> =>
    call("/api/category-groups", { method: "POST", body: JSON.stringify({ name, direction, forResale }) }),

  changeCategoryGroup: (id: string, changes: { name?: string; forResale?: boolean }): Promise<{ group: CategoryGroup }> =>
    call(`/api/category-groups/${id}`, { method: "POST", body: JSON.stringify(changes) }),

  deleteCategoryGroup: (id: string): Promise<{ ok: true }> => call(`/api/category-groups/${id}/delete`, { method: "POST" }),

  moveCategoryGroup: (id: string, by: -1 | 1): Promise<{ ok: true }> => call(`/api/category-groups/${id}/move`, { method: "POST", body: JSON.stringify({ by }) }),

  moveCategory: (id: string, by: -1 | 1): Promise<{ ok: true }> => call(`/api/categories/${id}/move`, { method: "POST", body: JSON.stringify({ by }) }),
};

const DIRECTION_LABELS: Record<Direction, string> = {
  money_in: "Funds incoming",
  money_out: "Funds outgoing",
  stock: "Inventory movements",
};

export function directionLabel(direction: Direction): string {
  return DIRECTION_LABELS[direction];
}

const ROLE_LABELS: Record<Role, string> = {
  sales_staff: "Sales staff",
  supervisor: "Supervisor",
  manager: "Manager",
  admin: "Administrator",
  master: "System administrator",
};

export function roleLabel(role: Role): string {
  return ROLE_LABELS[role];
}

/** Cents to the string a person reads, with thousands separators. */
export function formatMoney(value: number): string {
  const negative = value < 0;
  const absolute = Math.abs(value);
  const whole = Math.trunc(absolute / 100);
  const fraction = String(absolute % 100).padStart(2, "0");
  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}${grouped}.${fraction}`;
}

const ACCOUNT_KIND_LABELS: Record<AccountKind, string> = {
  bank: "Bank",
  cash: "Cash",
  crypto: "Crypto",
  card: "Card",
};

export function accountKindLabel(kind: AccountKind): string {
  return ACCOUNT_KIND_LABELS[kind];
}

export interface DuplicateGroup {
  readonly lines: readonly {
    readonly id: string;
    readonly postDate: string;
    readonly valueDate: string;
    readonly amountCents: number;
    readonly balanceAfterCents: number | null;
    readonly counterparty: string | null;
    readonly matched: boolean;
  }[];
  readonly keepId: string;
  readonly dropIds: readonly string[];
}

/** One movement of money, bank or cash (D, 10 October 2026). */
export interface MoneyLine {
  readonly id: string;
  readonly source: "bank" | "cash";
  readonly accountName: string;
  readonly accountKind: string;
  readonly day: string;
  readonly amountCents: number;
  readonly party: string;
  readonly description: string;
  readonly what: string;
  /** Its transaction category, for the accounts (D, 16 October 2026). */
  readonly category?: string | null;
  readonly saleId?: string | null;
  /** The sale, voucher or purchase behind it, item by item, with its photos (D, 15 October 2026). */
  readonly document?: {
    readonly number: string;
    readonly items: readonly string[];
    readonly photoIds: readonly string[];
    readonly pdfIds?: readonly string[];
    readonly discountCents?: number | null;
    readonly surchargeCents?: number | null;
  } | null;
}

export interface MoneyLines {
  readonly lines: readonly MoneyLine[];
  readonly total: number;
  readonly page: number;
  readonly pages: number;
  readonly inCents: number;
  readonly outCents: number;
}

function moneyQuery(filter: { account?: string; from?: string; to?: string; direction?: string; page?: number }): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) if (value !== undefined && value !== "") params.set(key, String(value));
  return params.toString();
}

/** One batch of a product as it was bought (D, 10 October 2026). */
export interface ProductBatch {
  readonly day: string;
  readonly how: string;
  readonly units: number;
  readonly costCents: number;
  readonly onHand: number;
  readonly purchaseNumber: string | null;
  readonly invoiceNo: string | null;
  readonly supplier: string | null;
  readonly voucherNumber: string | null;
}

/** What an invoice says about who sent it (D, 11 October 2026). */
export interface SupplierDetails {
  readonly address?: string | null;
  readonly phone?: string | null;
  readonly email?: string | null;
  readonly website?: string | null;
  readonly uen?: string | null;
}
